import { DetailedHTMLProps, FC, HTMLAttributes, useMemo } from "react";
import { useLogeMembers } from "../api/user-api";
import { LogeMember } from "../types/entities";

interface Props
  extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  userId: LogeMember["id"];
  showName?: boolean;
  bgColor?: BgColor;
  onClick?: () => void;
  selected?: boolean;
  size?: AvatarSize;
  /** Dim to ~30% opacity — used for "not selected" states (e.g. the fine payer picker). */
  dim?: boolean;
}

type AvatarSize = "xs" | "sm" | "md";
type BgColor = "slate" | "green" | "yellow" | "red";

const AvatarCircle: FC<Props> = ({
  userId,
  showName = true,
  onClick,
  selected = false,
  bgColor = "slate",
  size = "md",
  dim = false,
  ...rest
}) => {
  const { data: members } = useLogeMembers();
  const member = members?.find((m) => m.id === userId);
  const circleSize = useMemo(() => {
    if (size === "xs") return 18;
    if (size === "sm") return 30;
    return 48;
  }, [size]);

  const emojiSize = useMemo(() => {
    if (size === "xs") return "xs";
    if (size === "sm") return "xl";
    return "3xl";
  }, [size]);

  const setBgColor = useMemo(() => {
    switch (bgColor) {
      case "green":
        return "bg-vote-yes-soft";
      case "slate":
        return "bg-paper2";
      case "yellow":
        return "bg-vote-maybe-soft";
      // The app avoids a true saturated red everywhere else — cancelled
      // events, TBD badges — in favour of clay, a warmer, quieter rust. This
      // keeps "not attending" consistent with that rather than introducing
      // the one alarm-red note in an otherwise calm palette.
      case "red":
        return "bg-clay-soft";
    }
  }, [bgColor]);

  return (
    <div
      onClick={onClick}
      className={`flex flex-col items-center select-none ${onClick ? "cursor-pointer" : ""} ${
        dim ? "opacity-30" : ""
      }`}
      style={onClick ? { WebkitTapHighlightColor: "transparent" } : undefined}
      {...rest}
    >
      <div
        className={`flex ${setBgColor} justify-center items-center rounded-full ${
          selected ? "ring-2 ring-clay" : ""
        }`}
        style={{
          width: circleSize,
          height: circleSize,
        }}
      >
        <div className={`text-${emojiSize}`}>{member?.emoji}</div>
      </div>
      {showName && (
        <span className="text-[10px] font-mono tracking-wide text-ink3 mt-1.5">
          {member?.name}
        </span>
      )}
    </div>
  );
};

export default AvatarCircle;
